import { ReactNode } from 'react';
import Image from 'next/image';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@radix-ui/react-tabs';
import { ArrowRightIcon, ChevronRightIcon } from 'lucide-react';

import { ContentfulRichText } from '~/components/common/contentful-rich-text';
import { Link } from '~/components/common/link';
import { Button } from '~/components/ui/button';
import { flattenItems } from '~/lib/contentful';
import { cn, ctaLinkResolver } from '~/lib/utils';
import { BlockTabsFragment } from '~/server/generated/contentful-graphql';
import { italicizeStarWars } from '~/utils/italicize-star-wars';

interface TabsProps {
  block: BlockTabsFragment;
  renderContent?: (tab: any, index: number) => ReactNode;
  headlineClassName?: string;
}

export function ContentfulTabs({ block, renderContent, headlineClassName }: TabsProps) {
  const allTabs = flattenItems(block.tabsCollection?.items);
  // Filter to only tabs with headlines to ensure consistent indexing
  const tabs = allTabs.filter((tab) => tab.headline);

  // Get the first tab's value to use as default
  const defaultTabValue = tabs.length > 0 ? `${tabs[0].headline!.toLowerCase()}0` : '';

  if (tabs.length === 0) return null;

  return (
    <section className='overflow-x-hidden'>
      <div className='container'>
        <Tabs
          orientation='vertical'
          defaultValue={defaultTabValue}
          className='lg:bg-blue-light-50 gap-6 lg:flex lg:gap-x-12 lg:rounded-2xl lg:p-12 xl:gap-x-16'
        >
          <div
            className={cn(
              ' lg:w-76',
              'max-lg:before:bg-border max-lg:relative max-lg:before:absolute max-lg:before:-left-[calc((100vw-100%)/2)] max-lg:before:bottom-0 max-lg:before:-z-10 max-lg:before:h-px max-lg:before:w-screen',
            )}
          >
            <TabsList
              className={cn(
                'hide-scrollbar flex max-lg:mb-6 max-lg:gap-x-6 max-lg:overflow-x-auto lg:flex-col lg:gap-y-1',
                'max-lg:mask-[linear-gradient(to_right,transparent,black_24px,black_calc(100%-24px),transparent)] max-lg:-mx-6 max-lg:px-6',
              )}
            >
              {tabs.map((tab, idx) => (
                <TabsTrigger
                  className={cn(
                    'body-base text-muted-foreground flex items-center justify-between gap-x-5 py-3 text-left',
                    'max-lg:data-[state=active]:text-secondary max-lg:data-[state=active]:border-b-secondary max-lg:font-greycliff-bold max-lg:whitespace-nowrap max-lg:border-b max-lg:border-b-transparent',
                    'lg:data-[state=active]:bg-secondary lg:data-[state=active]:text-secondary-foreground lg:rounded-lg lg:px-4',
                  )}
                  key={tab.headline}
                  value={tab.headline!.toLowerCase() + idx}
                >
                  <span>
                    {italicizeStarWars(
                      tab.headline,
                      'not-italic max-lg:font-greycliff-boldoblique lg:font-greycliff-regularoblique',
                    )}
                  </span>
                  <ArrowRightIcon className='size-3 max-lg:hidden' />
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <div className='flex-1'>
            {tabs.map((tab, idx) => {
              const ctaPrimaryHref = ctaLinkResolver({
                ctaLink: tab.ctaLinkPrimary,
                ctaLinkContentType: tab.ctaLinkContentPrimaryCollection?.items[0]?.__typename,
                ctaLinkContentSlug: tab.ctaLinkContentPrimaryCollection?.items[0]?.slug,
                ctaLinkAssetUrl: tab.ctaLinkAssetPrimary?.url,
              });

              return (
                <TabsContent
                  key={tab.headline! + idx}
                  value={tab.headline!.toLowerCase() + idx}
                  className='mt-0 hidden flex-col gap-6 rounded-2xl data-[state=active]:flex'
                >
                  <div>
                    {tab.image && (
                      <div className='relative mb-6 overflow-hidden rounded-2xl'>
                        <Image
                          src={tab.image?.url || ''}
                          alt={tab.image.description || tab.headline + ' image'}
                          width={768}
                          height={360}
                          className='h-auto max-h-[165px] w-full object-cover sm:max-h-[220px] md:max-h-[280px] lg:max-h-[360px]'
                        />
                        <h3 className={cn('font-greycliff-extrabold absolute bottom-5 left-5 max-w-24 text-[30px] leading-tight text-white lg:bottom-10 lg:left-12 lg:max-w-60 lg:text-[64px] lg:leading-[72px]', headlineClassName)}>
                          {italicizeStarWars(tab.headline)}
                        </h3>
                      </div>
                    )}
                    {renderContent ? (
                      renderContent(tab, idx)
                    ) : (
                      <ContentfulRichText visualStyle='Default' json={tab.copy?.json} />
                    )}
                    {tab.ctaTitlePrimary && ctaPrimaryHref && (
                      <div className='mt-6'>
                        <Button variant='link' asChild className='text-blue-dark px-0'>
                          <Link href={ctaPrimaryHref}>
                            {tab.ctaTitlePrimary}
                            {tab.ctaTitlePrimary.trim().toLowerCase() === 'learn more' && (
                              <span className='sr-only'> about {tab.headline}</span>
                            )}{' '}
                            <ChevronRightIcon size={18} />
                          </Link>
                        </Button>
                      </div>
                    )}
                  </div>
                </TabsContent>
              );
            })}
          </div>
        </Tabs>
      </div>
    </section>
  );
}
